﻿import type { Feature, FeatureCollection, Point } from 'geojson';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { AirportMetarInfo } from '../services/actor-messages';
import { RunwayOBB } from './runway-geometry';
import {
    computeRunwayAxisWind,
    effectiveWindSpeed,
    getWindRenderParts,
    isReadingFlipped,
    runwayAxisRotation
} from '../airports/runway-wind';

const SOURCE_ID = 'fe-runway-wind-source';
const LAYER_ID = 'fe-runway-wind';

const WIND_MIN_ZOOM = 12;
const WIND_TEXT_SIZE = 14;
// Text offset in ems: [along-text-direction, perpendicular-to-text]. Positive y = below baseline.
const WIND_TEXT_OFFSET: [number, number] = [0, -1.5];
const WIND_TEXT_COLOR = '#e8e6fb';
const WIND_ARROW_FONT_SCALE = 1.5;
const WIND_COMPONENT_GAP = '    ';
const WIND_HALO_COLOR = '#000000';
const WIND_HALO_WIDTH = 0.5;


export interface WindGustSettings {
    useGust: boolean;
    gustFactor: number;
}

export class RunwayWindLayer {
    private map: MaplibreMap | null = null;

    onAdd(map: MaplibreMap, beforeLayerId: string): void {
        this.map = map;

        if (!map.getSource(SOURCE_ID)) {
            map.addSource(SOURCE_ID, {
                type: 'geojson',
                data: emptyCollection()
            });
        }

        if (!map.getLayer(LAYER_ID)) {
            map.addLayer(
                {
                    id: LAYER_ID,
                    type: 'symbol',
                    source: SOURCE_ID,
                    minzoom: WIND_MIN_ZOOM,
                    layout: {
                        'text-field': [
                            'format',
                            ['get', 'axialArrow'],
                            { 'font-scale': WIND_ARROW_FONT_SCALE },
                            ['get', 'axialValue'],
                            {},
                            ['get', 'sep'],
                            {},
                            ['get', 'crossArrow'],
                            { 'font-scale': WIND_ARROW_FONT_SCALE },
                            ['get', 'crossValue'],
                            {}
                        ],
                        'text-font': ['Metropolis Semi Bold'],
                        'text-size': WIND_TEXT_SIZE,
                        'text-rotate': ['get', 'rotation'],
                        'text-rotation-alignment': 'map',
                        'text-allow-overlap': true,
                        'text-ignore-placement': true,
                        'text-anchor': 'center',
                        'text-padding': 0,
                        'text-offset': WIND_TEXT_OFFSET
                    },
                    paint: {
                        'text-color': WIND_TEXT_COLOR,
                        'text-halo-color': WIND_HALO_COLOR,
                        'text-halo-width': WIND_HALO_WIDTH
                    }
                },
                beforeLayerId
            );
        }
    }

    onRemove(map: MaplibreMap): void {
        if (map.getLayer(LAYER_ID)) {
            map.removeLayer(LAYER_ID);
        }
        if (map.getSource(SOURCE_ID)) {
            map.removeSource(SOURCE_ID);
        }
        this.map = null;
    }

    update(obbs: RunwayOBB[], metarMap: Map<string, AirportMetarInfo>, gust: WindGustSettings): void {
        const map = this.map;
        if (!map) {
            return;
        }

        const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        if (!source) {
            return;
        }

        // Index metars by airport identifiers for fast lookup
        const metarByIcao = new Map<string, AirportMetarInfo>();
        const metarByIata = new Map<string, AirportMetarInfo>();
        const metarByFaa  = new Map<string, AirportMetarInfo>();
        for (const metar of metarMap.values()) {
            if (metar.icaoId) { metarByIcao.set(metar.icaoId, metar); }
            if (metar.iataId) { metarByIata.set(metar.iataId, metar); }
            if (metar.faaId)  { metarByFaa.set(metar.faaId, metar);  }
        }

        const features: Feature<Point>[] = [];
        const processedRunways = new Set<string | number>();

        for (const obb of obbs) {
            if (obb.featureId != null && processedRunways.has(obb.featureId)) {
                continue;
            }
            if (obb.featureId != null) {
                processedRunways.add(obb.featureId);
            }

            const icaoId = obb.properties.icaoid as string | undefined;
            const iataId = obb.properties.iataid as string | undefined;
            const faaId  = obb.properties.faaid  as string | undefined;

            const metar =
                (icaoId ? metarByIcao.get(icaoId) : undefined) ??
                (iataId ? metarByIata.get(iataId) : undefined) ??
                (faaId  ? metarByFaa.get(faaId)   : undefined);

            if (!metar || (metar.windSpeed == null && metar.windDirection == null)) {
                continue;
            }

            const speed = effectiveWindSpeed(metar, gust.useGust, gust.gustFactor);
            const adjustedMetar = speed !== metar.windSpeed ? { ...metar, windSpeed: speed } : metar;

            const wind   = computeRunwayAxisWind(obb.bearingDeg, adjustedMetar);
            const rotation = runwayAxisRotation(obb.bearingDeg);
            const flipped  = isReadingFlipped(obb.bearingDeg);
            const parts    = getWindRenderParts(wind, flipped);

            if (!parts) {
                continue;
            }

            const hasAxial = parts.axialArrow !== '' || parts.axialValue !== '';
            const hasCross = parts.crossArrow !== '' || parts.crossValue !== '';
            const sep = hasAxial && hasCross ? WIND_COMPONENT_GAP : '';

            features.push({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: obb.center },
                properties: {
                    axialArrow: parts.axialArrow,
                    axialValue: parts.axialValue,
                    sep,
                    crossArrow: parts.crossArrow,
                    crossValue: parts.crossValue,
                    rotation
                }
            });
        }

        source.setData({ type: 'FeatureCollection', features });
    }

    clear(): void {
        const source = this.map?.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        source?.setData(emptyCollection());
    }
}

function emptyCollection(): FeatureCollection {
    return { type: 'FeatureCollection', features: [] };
}
